import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ModalAssociarTemplate from '../../components/ModalAssociarTemplate';

const GerenciarCursos = () => {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCursos, setFilteredCursos] = useState([]);
  const [showModalTemplate, setShowModalTemplate] = useState(false);
  const [cursoSelecionado, setCursoSelecionado] = useState(null);
  const navigate = useNavigate();

  const fetchCursos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cursos');
      setCursos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCursos();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = cursos.filter(curso => 
        curso.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        curso.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        curso.categoria_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        curso.instrutor_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCursos(filtered);
    } else {
      setFilteredCursos(cursos);
    }
  }, [cursos, searchTerm]);

  const handleEditCurso = (cursoId) => {
    navigate(`/admin/cursos/${cursoId}/editar`);
  };

  const handleDeleteCurso = async (cursoId) => {
    if (window.confirm('Tem certeza que deseja excluir este curso?')) {
      try {
        await api.delete(`/cursos/${cursoId}`);
        await fetchCursos();
        setMessage({ type: 'success', text: 'Curso excluído com sucesso!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        console.error('Erro ao excluir curso:', error);
        setMessage({ type: 'error', text: 'Erro ao excluir curso. Tente novamente.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    }
  };

  const handleViewCurso = (cursoId) => {
    navigate(`/curso/${cursoId}`);
  };

  const handleViewStats = (cursoId) => {
    navigate(`/admin/cursos/${cursoId}/estatisticas`);
  };

  const handleCreateCurso = () => {
    navigate('/criar-curso');
  };

  const handleAssociarTemplate = (curso) => {
    setCursoSelecionado(curso);
    setShowModalTemplate(true);
  };

  const handleCloseModalTemplate = () => {
    setShowModalTemplate(false);
    setCursoSelecionado(null);
  };

  const handleSuccessTemplate = () => {
    fetchCursos(); // Recarregar cursos para atualizar dados
    setMessage({ type: 'success', text: 'Template associado com sucesso!' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Gerenciar Cursos</h1>
          <p className="text-slate-300 mt-2">Administre todos os cursos do sistema</p>
        </div>
        <Button 
          onClick={handleCreateCurso}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Criar Novo Curso
        </Button>
      </div>

      {/* Barra de Busca */}
      <div className="bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm rounded-lg p-4">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Buscar cursos por título, descrição, categoria ou instrutor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 hover:text-white"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="mt-2 text-sm text-slate-400">
            Mostrando {filteredCursos.length} de {cursos.length} cursos
          </p>
        )}
      </div>

      {/* Mensagem de Feedback */}
      {message.text && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/20 border-green-500/30 text-green-400' 
            : 'bg-red-900/20 border-red-500/30 text-red-400'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Lista de Cursos */}
      {filteredCursos.length === 0 ? (
        <Card className="p-12 text-center bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {searchTerm ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              )}
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum curso encontrado'}
          </h3>
          <p className="text-slate-300 mb-4">
            {searchTerm 
              ? `Nenhum curso corresponde à busca "${searchTerm}"` 
              : 'Comece criando seu primeiro curso'
            }
          </p>
          {searchTerm ? (
            <Button onClick={() => setSearchTerm('')} className="bg-slate-600 hover:bg-slate-700">
              Limpar Busca
            </Button>
          ) : (
            <Button onClick={handleCreateCurso} className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800">
              Criar Primeiro Curso
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCursos.map((curso) => (
            <Card key={curso.id} className="overflow-hidden hover:shadow-lg transition-shadow bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
              {/* Imagem do Curso */}
              <div className="aspect-video bg-slate-700/50 flex items-center justify-center">
                {curso.capa ? (
                  <img 
                    src={`${api.defaults.baseURL.replace('/api', '')}/uploads/${curso.capa}`} 
                    alt={curso.titulo}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-16 h-16 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </div>

              {/* Conteúdo do Card */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white line-clamp-2">
                    {curso.titulo}
                  </h3>
                  <Badge className="ml-2 bg-slate-600/20 text-slate-300 border border-slate-600/30">
                    {curso.nivel || 'Básico'}
                  </Badge>
                </div>

                <p className="text-slate-300 text-sm mb-4 line-clamp-3">
                  {curso.descricao}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm text-slate-400">
                    <span>📚 {curso.total_alunos || 0} alunos</span>
                    <span>Criado: {formatDate(curso.created_at)}</span>
                  </div>
                  {curso.categoria_nome && (
                    <div className="flex items-center text-sm text-slate-400">
                      <span>🏷️ {curso.categoria_nome}</span>
                    </div>
                  )}
                  {curso.instrutor_nome && (
                    <div className="flex items-center text-sm text-slate-400">
                      <span>👨‍🏫 {curso.instrutor_nome}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-slate-400">
                    <span>📄 Template: {curso.template_certificado_id ? 'Personalizado' : 'Padrão'}</span>
                  </div>
                </div>

                {/* Ações */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleViewCurso(curso.id)}
                      size="sm"
                      className="flex-1 bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Ver
                    </Button>
                    <Button
                      onClick={() => handleEditCurso(curso.id)}
                      size="sm"
                      className="flex-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/50"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Editar
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleViewStats(curso.id)}
                      size="sm"
                      className="flex-1 bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/50"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Stats
                    </Button>
                    <Button
                      onClick={() => handleAssociarTemplate(curso)}
                      size="sm"
                      className="flex-1 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 border border-yellow-600/50"
                      title="Associar Template de Certificado"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Template
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleDeleteCurso(curso.id)}
                      size="sm"
                      className="flex-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Excluir
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal para Associar Template */}
      <ModalAssociarTemplate
        isOpen={showModalTemplate}
        onClose={handleCloseModalTemplate}
        curso={cursoSelecionado}
        onSuccess={handleSuccessTemplate}
      />
    </div>
  );
};

export default GerenciarCursos; 